import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import fallbackTranslation from "@/lib/dbTranslations";
import { getSortedCart, getSortedWholeCart } from "@/common/utils/cartFunction";
// COMPONENTS
import SquareColorSelector from "@/components/elements/SquareColorSelector/SquareColorSelector";
import InputWithButtons from "@/components/elements/InputWithButtons/InputWithButtons";
import { useTranslation } from "next-i18next";
// CONTEXT
import { useApp } from "@/components/context/AppContext/AppContext";
import { useProductCart, useSetProductCart } from "@/components/context/ProductCartContext/ProductCartContext";
// BOOTSTRAP
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
// ICONS
import { TrashIcon } from "@/public/icons/icons";
// STYLE
import s from "./MoreDetailsTable.module.scss";
import { ProductStatus } from "@/components/core/Classes/ProductStatus";

export default function MoreDetailsTable({ cartMode, quantityPerSize, setQuantityPerSize, price, product, modifyQuantity, variant }) {
  const { t } = useTranslation(["common", "db-translations"]);
  const app = useApp();
  const router = useRouter();

  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);

  // Set colors and sizes from app context, check if values are ready.
  useEffect(() => {
    if (app.appConfig?.colors && app.appConfig?.sizes) {
      setColors(app.appConfig?.colors);
      setSizes(app.appConfig?.sizes);
    }
  }, [app]);

  // ProductCart Context
  const productCart = useProductCart();
  const setProductCart = useSetProductCart();

  const discountedPrice = product?.Prezzo - product?.Prezzo * (product?.Sconto / 100);

  // Disable form when product is in presale
  let presaleMode;
  let actionButtonNumber;
  if (ProductStatus.find((status) => status.key == "PRESALE").id == product?.Stato) {
    actionButtonNumber = product["Confezione"] || 1;
    presaleMode = true;
  } else {
    actionButtonNumber = 1;
    presaleMode = false;
  }

  // TODO: Should create a common function used in many places, this function is used in other components too
  // Remove selected line from the table, second parameter delete from context (boolean)
  const handleRemoveItem = (selectedItem, deleteFromContext) => {
    let itemsToSpread = productCart;

    let currentQuantityPerSize = quantityPerSize.filter((item) => (presaleMode ? item.color != selectedItem.color : item != selectedItem));
    let cartItem = productCart.find((selectedProduct) => {
      return selectedProduct.productId == product?.["_id"];
    });

    // If there are no elements in quantityPerSize, we will remove the product object from the array and put the new array in itemsToSpread
    if (currentQuantityPerSize.length === 0) {
      itemsToSpread = productCart.filter((item) => {
        return item.productId !== product?.["_id"];
      });

      // Update quantityPerSize with the new filtered array checking item object, update productCart with the new filtered array checking product id
      setProductCart(itemsToSpread);
    } else if (cartItem) {
      // Update quantityPerSize, and productCart states with the new filtered array
      cartItem.selectedItemQuantity = currentQuantityPerSize;
      // The above line update productCart context every time user remove a line (used in checkoutPage).
      // In order to execute deleteFromContext must be true
      deleteFromContext && setProductCart([...productCart]);
    }
    setQuantityPerSize(getSortedCart([...currentQuantityPerSize], colors, sizes));
  };

  let handleDisabled = true;
  if (product?.["Stato"] == 0 || product?.["Stato"] == 2) {
    handleDisabled = true;
  } else {
    handleDisabled = false;
  }

  // Create the items table
  const itemsRows = quantityPerSize?.map((selectedItem, index) => {
    // When using items table with inputWithButtons component, the function above will change the state and the productCart context. This is used in checkout page
    const onChangeQuantity = ({ quantity: inputQuantity, size: selectedSize }) => {
      let currentQuantityPerSize = quantityPerSize;
      // If same selectedColor and selectedSize must update instead of plain add
      if (
        quantityPerSize.filter((item) => {
          return item.color == selectedItem.color && item.size == selectedSize;
        })?.length > 0
      ) {
        // gets the current quantity removing duplication
        currentQuantityPerSize = quantityPerSize.filter((item) => {
          return item.color !== selectedItem.color || item.size !== selectedItem.size;
        });
      }

      const itemsToSpread = productCart.filter((item) => {
        return item.productId !== product?.["_id"];
      });
      // Save the original creation date in order to use it again when updating the selected product cart
      const previousCreationDate = productCart.find((item) => {
        return item.productId == product?.["_id"];
      }).creationDate;

      // Initialized new quantity size array
      let updatedQuantityPerSize = [{ quantity: inputQuantity, color: selectedItem.color, size: selectedSize, notAvailable: false }];

      // Check if presale mode and update each size with current quantity
      if (presaleMode) {
        updatedQuantityPerSize = [];
        currentQuantityPerSize = currentQuantityPerSize.filter((item) => item.color != selectedItem.color);
        product.Taglie.map((taglia) => {
          updatedQuantityPerSize.push({ quantity: inputQuantity, color: selectedItem.color, size: taglia, notAvailable: false });
        });
      }

      const updatedSelectedQuantity = [...currentQuantityPerSize, ...updatedQuantityPerSize];

      // Update cart context with updated quantity per size, sorted by color and sizes.
      setProductCart(
        getSortedWholeCart([
          ...itemsToSpread,
          {
            productId: product?.["_id"],
            selectedItemQuantity: updatedSelectedQuantity,
            creationDate: previousCreationDate,
          },
        ])
      );
      // Update QuantityPerSize state sorted by sizes. (by Id)
      setQuantityPerSize(getSortedCart(updatedSelectedQuantity, colors, sizes));
    };

    // Calculate on row amount, if discounted or not.
    let amount = 0;
    if (product?.Sconto) {
      amount = discountedPrice * selectedItem.quantity;
    } else {
      amount = price * selectedItem.quantity;
    }

    return (
      <tr key={index} className={`fs-7 ${s.row_wrapper}`}>
        <td className={s.table_cell}>
          <div className="d-flex align-items-center">
            <SquareColorSelector index={index} color={selectedItem.color} placement="bottom" square_size="square-s" tooltipText={true} />
            <span className="ps-2 d-none d-md-flex">
              {colors &&
                fallbackTranslation(
                  "colors",
                  "db-translations",
                  colors.find((el) => selectedItem.color == el?.key),
                  t,
                  router.locale
                )}
            </span>
          </div>
        </td>
        <td className={s.table_cell_text}>
          <p className={s.table_cell_span}>
            {sizes &&
              fallbackTranslation(
                "sizes",
                "db-translations",
                sizes.find((el) => selectedItem.size == el?.key),
                t,
                router.locale
              )}
          </p>
        </td>
        <td className={s.table_cell_text}>
          {!modifyQuantity ? (
            <p className={s.table_cell_span}>{selectedItem.quantity}</p>
          ) : (
            <div className={s.inputWithButtons_wrapper}>
              <InputWithButtons
                actionButtonNumber={actionButtonNumber}
                disableForm={presaleMode}
                cartMode={cartMode}
                handleDisabled={handleDisabled || selectedItem.notAvailable}
                index={index}
                onChangeQuantity={onChangeQuantity}
                quantityPerSize={quantityPerSize}
                selectedColor={selectedItem.color}
                selectedSize={selectedItem.size}
                variant={variant}
              />
            </div>
          )}
        </td>
        <td className={s.table_cell_text}>
          <p className={s.table_cell_span}>{product?.Sconto ? discountedPrice?.toFixed(2) + " €" : price?.toFixed(2) + " €"}</p>
        </td>
        <td className={s.table_cell_text}>
          <p className={s.table_cell_span}>{amount.toFixed(2) + " €"}</p>
        </td>
        <td className={s.table_cell}>
          <Button className="p-0" onClick={() => handleRemoveItem(selectedItem, cartMode ? true : false)} variant="link">
            <TrashIcon width="24" height="24" color={selectedItem.notAvailable ? "red" : "black"} />
          </Button>
        </td>
      </tr>
    );
  });

  // Table header
  const table =
    quantityPerSize?.length > 0 ? (
      <Table size="sm">
        <thead className="fs-7">
          <tr className={s.row_wrapper}>
            <th className={`${s.table_header}`}>{t("common.color")}</th>
            <th className={`${s.table_header}`}>{t("common.size")}</th>
            <th className={`${s.table_header}`}>{t("common.qty")}</th>
            <th className={`${s.table_header}`}>{t("common.price")}</th>
            <th className={`${s.table_header}`}>{t("common.total")}</th>
          </tr>
        </thead>
        <tbody>{itemsRows}</tbody>
      </Table>
    ) : (
      ""
    );

  return <Container className="details_table pt-3 px-0">{table}</Container>;
}
